import type { ApiClient } from '@carbonwebmail/api-client'
import type { EmailProviderDto, GoogleOauthOutcome } from '@carbonwebmail/contracts'
import {
  dailyCapFor,
  EMAIL_PROVIDERS,
  isSignedInProvider,
  PROVIDER_FIELDS,
} from '@carbonwebmail/domain'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import type { AlertTone } from '../../ui/Alert.js'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { CreateCard } from '../../ui/CreateCard.js'
import { Field, SelectField } from '../../ui/Field.js'
import { when } from '../../ui/format.js'
import type { TestOutcome } from './useEmailProvider.js'
import { useEmailProvider } from './useEmailProvider.js'

/**
 * Which service this organisation's mail leaves through.
 *
 * Three things are said out loud, because each is something a customer would
 * otherwise discover late and badly.
 *
 * **Your own provider means your own reputation.** Until an organisation
 * configures one, its mail goes through ours — which works, and means its
 * delivery rises and falls with everybody else's. The screen says which of the
 * two it is rather than leaving it to be inferred from a blank form.
 *
 * **Credentials are never shown back.** They are stored sealed and there is no
 * route that returns one, so the form is empty even when a provider is
 * configured, and saving replaces the whole record. The screen says so, because
 * a form that looks empty when something is saved otherwise reads as broken.
 *
 * **A test send is offered immediately.** It is the only way somebody learns
 * whether what they just typed works — the alternative is finding out three
 * days later from an empty campaign report, which is exactly the failure this
 * product exists to prevent.
 */

/**
 * Keyed by the contract's own union, so adding a provider to the domain fails
 * the build here until somebody decides what to call it. A
 * `Record<string, string>` would have quietly rendered the raw value.
 */
const PROVIDER_LABELS: Record<EmailProviderDto, string> = {
  ses: 'Amazon SES',
  postmark: 'Postmark',
  resend: 'Resend',
  brevo: 'Brevo',
  smtp: 'SMTP',
  gmail: 'Gmail',
}

/**
 * What each provider is for, said before somebody picks one.
 *
 * Gmail is the one that needs it: it is the easiest to connect and the one
 * least suited to a list, and somebody who chooses it for four thousand
 * contacts finds out at message 501. Better to say so beside the option.
 */
const PROVIDER_NOTES: Partial<Record<EmailProviderDto, string>> = {
  gmail: 'Sign in with Google. 500 messages a day — good for a small list, not for a large one.',
  ses: 'No practical daily limit once your account is out of the sandbox.',
}

const PROVIDER_OPTIONS: readonly (readonly [string, string])[] = EMAIL_PROVIDERS.map((provider) => [
  provider,
  PROVIDER_LABELS[provider],
])

/**
 * What came back from Google's round trip.
 *
 * `createGoogleOauthRoutes` ends every path it can take — the five in
 * `GOOGLE_OAUTH_OUTCOMES` — by redirecting here with `?google=<outcome>`. Nothing
 * read that parameter until 8 Oct 2026, so connecting an account and failing to
 * connect one looked exactly alike: the page simply reappeared, unchanged, and
 * the only way to tell which had happened was to notice whether the card above
 * had changed wording. Somebody reported it as "I don't know if it connected",
 * which is precisely what the screen was saying.
 *
 * An outcome the server does not send is not rendered at all, rather than shown
 * as a word nobody wrote.
 */
const GOOGLE_OUTCOMES: Record<
  GoogleOauthOutcome,
  { readonly tone: AlertTone; readonly text: string }
> = {
  connected: {
    tone: 'ok',
    text: 'Google account connected. Your mail goes through it from now on — send a test below to be sure it works.',
  },
  cancelled: {
    tone: 'unknown',
    text: 'You stopped at Google’s screen, so nothing changed. Your mail still goes out through ours.',
  },
  expired: {
    tone: 'unknown',
    text: 'That took long enough for the request to expire, so nothing was connected. Starting again will work.',
  },
  unavailable: {
    tone: 'danger',
    text: 'Signing in with Google is not set up on this deployment, so there was nothing to connect to.',
  },
  failed: {
    tone: 'danger',
    text: 'Google would not finish the connection, so nothing was saved and your mail still goes out through ours.',
  },
}

/**
 * Reads that word once, then takes it off the URL.
 *
 * Captured on the first render rather than read on every one, because the
 * effect below removes it: without the capture the message would appear and
 * vanish in the same breath. Removing it means a refresh does not re-announce a
 * connection that happened a minute ago.
 */
function useGoogleOutcome() {
  const [params, setParams] = useSearchParams()
  const outcome = params.get('google')
  const [announced] = useState(() =>
    outcome === null ? undefined : GOOGLE_OUTCOMES[outcome as GoogleOauthOutcome],
  )

  useEffect(() => {
    if (outcome === null) return
    const next = new URLSearchParams(params)
    next.delete('google')
    setParams(next, { replace: true })
  }, [outcome, params, setParams])

  return announced
}

export function EmailProviderPage({ client }: { readonly client: ApiClient }) {
  const provider = useEmailProvider({ client })
  const { state, saved, problem, busy, test } = provider
  const google = useGoogleOutcome()

  const [chosen, setChosen] = useState<EmailProviderDto>('postmark')
  const [values, setValues] = useState<Record<string, string>>({})

  const change = useCallback((key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }))
  }, [])

  const submit = useCallback(async () => {
    // Cleared the moment they are accepted. Holding an API key in component
    // state for longer than the request takes buys nothing.
    if (await provider.save(chosen, values)) setValues({})
  }, [provider, chosen, values])

  return (
    <>
      <header className="mb-6">
        <p className="mt-1 text-[13px] text-t2">
          The service your mail actually leaves through. Using your own account means your delivery
          reputation is yours rather than shared with every other customer here.
        </p>
      </header>

      {google === undefined ? null : <Alert tone={google.tone}>{google.text}</Alert>}
      {problem === undefined ? null : <Alert>{problem}</Alert>}
      {saved ? <Alert tone="ok">Saved. Send a test to check it works.</Alert> : null}
      {test === undefined ? null : <TestResult outcome={test} />}

      <Async
        state={state}
        skeleton={<p className="text-[13px] text-t3">Loading…</p>}
        onRetry={() => void provider.retry()}
      >
        {(configured) => (
          <>
            <section className="mb-6 max-w-2xl rounded-card border border-line bg-surface p-5">
              <h2 className="mb-2 font-bold text-[15px] text-t1">Sending through</h2>
              {configured.provider === null ? (
                <p className="text-[13px] text-t2">
                  Our provider. Your mail goes out alongside every other customer here, which works
                  and means your delivery rises and falls with theirs. Set up your own below.
                </p>
              ) : (
                <>
                  <p className="text-[13px] text-t2">
                    Your own {PROVIDER_LABELS[configured.provider]} account, saved{' '}
                    {configured.updatedAt === null ? 'recently' : when(configured.updatedAt)}.
                  </p>
                  {/*
                    Which account, not just which provider.
                    *
                    Gmail is the only provider that answers this, and it is the
                    difference between "something is connected" and knowing it
                    is the right mailbox. It is also the address every campaign
                    has to be From: Gmail refuses anything else.
                  */}
                  {configured.sendsAs === null ? null : (
                    <p className="mt-1 text-[13px] text-t2">
                      Sending as <span className="font-medium text-t1">{configured.sendsAs}</span>.
                      Campaigns From any other address are refused by Gmail unless it is an alias
                      verified inside that account.
                    </p>
                  )}
                  <div className="mt-4 flex gap-3">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void provider.sendTest()}
                      className="font-medium text-[13px] text-brand disabled:opacity-60"
                    >
                      Send a test to me
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void provider.clear()}
                      className="font-medium text-[13px] text-t3 hover:text-danger disabled:opacity-60"
                    >
                      Go back to ours
                    </button>
                  </div>
                </>
              )}
            </section>

            <CreateCard
              heading={configured.provider === null ? 'Use your own' : 'Replace these credentials'}
              submit="Save"
              disabled={busy}
              onSubmit={() => void submit()}
            >
              <p className="mb-4 text-[13px] text-t2">
                We store these sealed and never show them again, so this form is empty even when a
                provider is set up. Saving replaces everything.
              </p>

              <SelectField
                id="provider"
                label="Provider"
                value={chosen}
                disabled={busy}
                options={PROVIDER_OPTIONS}
                onChange={(value) => setChosen(value as EmailProviderDto)}
              />

              {PROVIDER_NOTES[chosen] === undefined ? null : (
                <p className="mt-1 text-[12px] text-t3">{PROVIDER_NOTES[chosen]}</p>
              )}

              {/*
                A button, not a form.
                *
                Gmail's credential arrives from Google's consent screen and is
                sealed without ever being shown, so there is nothing here for
                anybody to paste. Drawing the two fields it stores would be
                asking for values nobody has.
              */}
              {isSignedInProvider(chosen) ? (
                <ConnectWithGoogle busy={busy} client={client} />
              ) : (
                PROVIDER_FIELDS[chosen].map((field) => (
                  <Field
                    key={field.key}
                    id={`provider-${field.key}`}
                    label={field.label}
                    // A secret is a password box: it keeps the value off a
                    // shoulder and out of a screen share.
                    type={field.secret ? 'password' : 'text'}
                    autoComplete="off"
                    value={values[field.key] ?? ''}
                    disabled={busy}
                    {...(field.required ? {} : { optional: true })}
                    {...(field.hint === '' ? {} : { hint: field.hint })}
                    onChange={(value) => change(field.key, value)}
                  />
                ))
              )}
            </CreateCard>
          </>
        )}
      </Async>
    </>
  )
}

/**
 * `unknown` is its own answer and never the red one. (I7)
 *
 * A provider that did not respond in time has said nothing about the
 * credentials, and telling somebody it failed sends them off to regenerate an
 * API key that was fine.
 */
function TestResult({ outcome }: { readonly outcome: TestOutcome }) {
  if (outcome.status === 'sent') {
    return <Alert tone="ok">Sent. Check your inbox — it may take a minute to arrive.</Alert>
  }

  if (outcome.status === 'unknown') {
    return (
      <Alert tone="unknown">
        Your provider did not answer in time, which says nothing either way about the credentials.
        {outcome.detail === undefined ? null : ` It said: ${outcome.detail}`}
      </Alert>
    )
  }

  if (outcome.status === 'not-configured') {
    return <Alert>{outcome.detail ?? 'There are no credentials to test yet.'}</Alert>
  }

  return (
    <Alert>
      Your provider refused the message.
      {outcome.detail === undefined ? null : ` It said: ${outcome.detail}`}
    </Alert>
  )
}

/**
 * Connecting Gmail, which ends in a navigation rather than a form submission.
 *
 * The browser still has to *leave* for Google's consent screen and come back
 * to the callback — an XHR cannot do that, and the callback is what seals the
 * credential and sends them back here with a word about how it went.
 *
 * What changed is how the trip starts. It was a plain `<a href="/api/google/
 * start">`, which worked while the credential was a cookie the browser
 * attached to any navigation. It is an `Authorization` header now (ADR 0004),
 * so the link would arrive anonymous: the url is fetched first, with the
 * header, and then followed.
 */
function ConnectWithGoogle({
  busy,
  client,
}: {
  readonly busy: boolean
  readonly client: ApiClient
}) {
  const [starting, setStarting] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)

  const start = useCallback(async () => {
    setStarting(true)
    setProblem(undefined)
    try {
      const url = await client.connectGoogleUrl()
      if (url === undefined) {
        setProblem('Connecting Gmail is not switched on for this deployment.')
        return
      }
      window.location.href = url
    } catch {
      setProblem('We could not reach Google just now. Please try again in a moment.')
    } finally {
      setStarting(false)
    }
  }, [client])

  return (
    <div className="mt-4 rounded-card border border-line bg-surface-2 p-4">
      <p className="mb-3 text-[13px] text-t2 leading-relaxed">
        Sign in with Google and Carbon Mail will send through that account. It asks for permission
        to <strong className="text-t1">send mail only</strong> — never to read it — and you can
        revoke it from your Google account at any time.
      </p>
      {problem === undefined ? null : <Alert>{problem}</Alert>}
      <button
        type="button"
        disabled={busy || starting}
        className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
        onClick={() => void start()}
      >
        {starting ? 'Opening Google…' : 'Sign in with Google'}
      </button>
      <p className="mt-3 text-[12px] text-t3">
        Gmail accepts {dailyCapFor('gmail')} messages a day. Carbon Mail stops before that rather
        than having Google refuse them — for a larger list, choose Amazon SES.
      </p>
    </div>
  )
}
